(ns frontend.react.virtualized.scrollable-grid.kanban
  (:require
   [clojure.string :as string]
   [common.attribute :as attribute]
   [common.currencies :as currencies]
   [common.entity :as entity]
   [common.localization :as loc]
   [common.schema.entities.pipeline :as pipeline]
   [common.schema.entity-creator.utils :as ecu]
   [common.schema.registry :as registry]
   [common.url :as url]
   [common.util :as cu]
   [frontend.actions.entity :as entity-actions]
   [frontend.bootstrap.components :as rbs]
   [frontend.local-storage :as ls]
   [frontend.logging :as logging]
   [frontend.react.virtualized.scrollable-grid.common :as grid]
   [frontend.routing :as routing]
   [frontend.state.current-session :as session]
   [frontend.state.permissions :as f-permissions]
   [frontend.tenant-customizations :as tenant-customizations]
   [frontend.utils :as fu]
   [frontend.view.components.switch-toggle :as switch]
   [frontend.view.entity :as f-entity]
   [frontend.view.field-templates :as templates]
   [frontend.view.modals.dynamic.view :as dynamic]
   [frontend.view.modals.events :as modals]
   [oops.core :as oops]
   [potpuri.core :as potpuri :refer [fn->]]
   [reagent.core :as r]
   [ui.components.button.view :as button]
   [ui.components.radix-ui.dropdown-menu.view :as dropdown-menu]
   [ui.components.select.select :as select]
   [ui.components.toggle.view :as toggle]
   [ui.dom-globals :as dg]
   [ui.editors.data-input :as data-input]
   [clojure.set :as set]))

;; These constants are added to the event object
;; hence special naming to avoid confusion in code
(def ALLOWED-DROP-EFFECTS ["move" "copyMove" "all"])

(def numerics
  [:data.type/currency-value :data.type/datetime :data.type/date :data.type/integer
   :data.type/percentage :data.type/multiplier :data.type/long :data.type/number])

(defn recompute-list!
  [list-state]
  (let [{:keys [cache list-component]} @list-state
        clear-fn (when cache
                   (aget cache "clearAll"))
        update-grid-fn (when list-component
                         (aget list-component "forceUpdateGrid"))]
    (when (or clear-fn update-grid-fn)
      ;; Adding timeout here to avoid updating state while render is still ongoing
      (js/setTimeout (fn []
                       (when clear-fn
                         (oops/ocall cache "clearAll"))
                       (when update-grid-fn
                         (oops/ocall list-component "forceUpdateGrid")))
                     10))))

(defn recompute-all!
  [{:keys [setup-args]}]
  (let [lists-state (-> setup-args
                        :kanban-args
                        :lists-state)
        lists @lists-state]
    (doseq [[list-id _state] lists]
      (recompute-list! (r/cursor lists-state [list-id])))))

(defn list-entities
  [setup-args list-id]
  (let [kanban-args (:kanban-args setup-args)
        {:keys [sorted-kanban-entities-state grouped-by-state default-grouped-by]} kanban-args
        entities @sorted-kanban-entities-state
        tenant @session/tenant
        tenant-schema @session/tenant-schema
        attribute (or @grouped-by-state
                      default-grouped-by)]
    (->> entities
         (filter (fn [e]
                   (let [v (fu/get-value tenant tenant-schema e attribute)]
                     (cond (nil? v) (nil? list-id)
                           (set? v) (some v [list-id])
                           :else
                           (= v list-id))))))))

(defn attribute-view
  [{:keys [entity attribute show-labels? show-full-text? max-length]}]
  (let [tenant @session/tenant
        tenant-schema @session/tenant-schema
        valid-attribute-for-this-data? (templates/valid-attribute? attribute entity)
        raw-value (fu/get-value tenant tenant-schema entity attribute)
        fs (registry/get-field-schema tenant-schema attribute)
        data-type (:field/data-type fs)
        display-value (fu/display-data tenant-schema tenant attribute fs entity)
        str-value (str display-value)
        value (cond
                (and (not (cu/blank-value? str-value))
                     (some #{:company/logo}
                           [attribute
                            (when (coll? attribute)
                              (last attribute))]))
                [:img.company-logo
                 {:src (fu/sanitize-img-url str-value)
                  :alt (loc/tr :company/logo)}]

                (and (not show-full-text?)
                     (> (count str-value) max-length))
                (str (subs str-value 0 max-length) "...")

                :else
                str-value)
        label (or (-> fs
                      :field/translations
                      :en)
                  (:field/name fs)
                  (loc/tr attribute)
                  (loc/field-name attribute fs))
        field-entity-types (:field/entity-types fs)
        open-entity?
        (and (= data-type :data.type/ref-single)
             (some (set field-entity-types)
                   [:entity.type/company :entity.type/person :entity.type/investor
                    :entity.type/oppo
                    :entity.type/company :entity.type/real-estate :entity.type/other-asset
                    :entity.type/fundraising-opportunity
                    :entity.type/fund-commitment
                    :entity.type/fund
                    :entity.type/holding])
             (not (string/blank? (str raw-value))))
        open-url? (and (= data-type :data.type/text-url)
                       (not= :editor.type/image
                             (->> fs
                                  :field/default-visibility
                                  :editor))
                       (not (string/blank? (str raw-value))))
        ;;add missing https:
        href-value (when open-url?
                     (url/relative-or-protocol-url (str raw-value) :https))
        secondary-attribute (:field/secondary-attribute fs)
        secondary-fs (when secondary-attribute
                       (registry/get-field-schema tenant-schema secondary-attribute))
        secondary-value (when secondary-attribute
                          (let [secondary-path (if (coll? attribute)
                                                 (concat (butlast attribute)
                                                         [secondary-attribute])
                                                 secondary-attribute)]
                            (when (= (:field/data-type secondary-fs) :data.type/currency-symbol)
                              (let [currency-kw (fu/get-value tenant tenant-schema entity secondary-path)
                                    tenant-currency-kw (:tenant/default-currency @session/tenant)]
                                (cond (or (tenant-customizations/alj?)
                                          (tenant-customizations/eq-varainhoito?))
                                      (currencies/get-label (or currency-kw tenant-currency-kw))
                                      ;;show currency only if set and differs from tenant-currency:
                                      (and currency-kw
                                           (not= currency-kw tenant-currency-kw))
                                      (currencies/get-label currency-kw))))))]
    (when (and valid-attribute-for-this-data?
               (some? raw-value))
      [:div
       {:class "flex flex-col items-start gap-1 py-1 text-xs text-base-light-text-paler *:!py-0"}
       (when show-labels?
         [:div
          label])
       [button/secondary-open
        {:class    "max-w-full !px-0 [&_span]:!block [&_a]:!block"
         :tooltip  secondary-value
         :on-click (when open-entity?
                     (fn [_]
                       (routing/open-entity raw-value)))}
        (if open-url?
          [:a
           {:class  "mb-0 max-w-full !block truncate text-xs font-regular"
            :href   href-value
            :target "_blank"}
           value]
          [:span
           {:class "mb-0 max-w-full !block truncate text-xs font-normal"}
           value])
        (when secondary-value
          [:span
           {:class "mb-0 max-w-full !block truncate text-xs font-normal"}
           (str " " secondary-value)])]])))

(defn owner-circle
  [owner tooltip-text]
  (let [pic-url (fu/only-https-or-relative-img (:account/picture-url owner))]
    [button/secondary-open
     {:tooltip tooltip-text
      :class   "p-0"}
     [:div
      {:class "flex justify-center items-center
               w-6 h-6
               text-base-gloomy-text text-xs font-semibold
               border border-solid border-base-light-border-paler
               bg-base-gloomy rounded-full"}
      (if pic-url
        [:span
         {:class "block w-full h-full
                  bg-no-repeat bg-cover bg-center rounded-full"
          :style {:background-image (str "url('" (fu/sanitize-img-url pic-url) "')")}}]
        [:span
         (string/trim (str (first (:account/first-name owner))
                           ""
                           (first (:account/last-name owner))))])]]))

(defn kanban-card
  [{:keys [setup-args card-actions entity list-state long-card? attributes classes]}]
  (let [entity-id (:db/id entity)
        tenant @session/tenant
        tenant-schema @session/tenant-schema
        kanban-args (:kanban-args setup-args)
        {:keys [kanban-state card-settings-state]} kanban-args
        selected? (= (:selected-id @kanban-state)
                     entity-id)
        {:keys [max-length hide-owner? footer-1 footer-2 show-labels? show-full-text?]}
        @card-settings-state
        show-labels? (cu/default-boolean true show-labels?)
        owner-value (when-not hide-owner?
                      (fu/get-owner entity))
        owner-name (entity/display-string owner-value)
        owner-text (loc/tr :entity-type-owner [(loc/tr (:entity/type entity))])
        owner-tooltip (str owner-text ": " owner-name)
        footer-1-value (when footer-1
                         (fu/get-value tenant tenant-schema entity footer-1))
        footer-2-value (when footer-2
                         (fu/get-value tenant tenant-schema entity footer-2))
        max-length (or max-length 500)
        actions (->> card-actions
                     (remove (fn [{:keys [hidden?]}]
                               (when (fn? hidden?)
                                 (hidden? {:entity entity})))))
        visible-attributes (->> attributes
                                (remove (fn-> (partial registry/get-field-schema tenant-schema)
                                              :field/secondary-field?)))                            ;; do not show secondary attributes standalone
        {:keys [vertical-scrollbar?]} @list-state]
    [:div
     {:class-name (fu/cs "ml-2 mr-4 py-2 mt-2 px-3 bg-base-light cursor-pointer border border-solid rounded group"
                         (when vertical-scrollbar?
                           "has-scrollbar")
                         (when selected?
                           "border border-solid border-base-light-border selected")
                         classes)}
     [:div
      {:class "flex justify-between items-center gap-2"}
      [:div
       {:class "w-10/12 flex gap-2 items-center"}
       [:div
        {:class "max-w-full w-full truncate font-semibold"}
        (entity/resolve-entity-name entity session/get-entity session/reverse-lookup)]]

      [:div
       (cond
         (empty? actions)
         nil

         (= (count actions) 1)
         (let [{:keys [label icon on-select!]} (first actions)]
           [button/square-secondary-open
            {:class    "invisible group-[.selected]:visible"
             :tooltip  label
             :on-click (fn [e]
                         (on-select!
                          {:e      e
                           :entity entity}))}
            [:i
             {:class-name (fu/cs icon)}]])

         :else
         [dropdown-menu/view
          {:trigger [:i.fal.fa-ellipsis-v.text-xl]
           :items   (->> actions
                         (mapv (fn [card-action]
                                 (let [{:keys [label icon on-select!]} card-action]
                                   {:label      label
                                    :icon-class icon
                                    :on-click   (fn [e]
                                                  (on-select!
                                                   {:e              e
                                                    :recompute-all! (partial recompute-all! {:setup-args setup-args})
                                                    :entity         entity}))})))
                         (doall))}])]]
     (when long-card?
       [:div
        {:class "pt-3
                 min-h-8"}
        ;; do not show secondary fields standalone in card
        (doall (for [attribute visible-attributes]
                 ^{:key (str "long-card-" attribute)}
                 [attribute-view
                  {:entity          entity
                   :attribute       attribute
                   :show-labels?    show-labels?
                   :show-full-text? show-full-text?
                   :max-length      max-length}]))])
     ;;footer
     (when (and long-card?
                ;;don't show empty footer:
                (or (and (not hide-owner?) owner-value)
                    (and footer-1 footer-1-value)
                    (and footer-2 footer-2-value)))
       [:div.kanban-card-footer.d-inline-flex.align-items-center.justify-content-between

        [:div.kanban-card-footer-attribute
         (when footer-1
           [attribute-view
            {:entity          entity
             :attribute       footer-1
             :show-labels?    show-labels?
             :show-full-text? show-full-text?
             :max-length      max-length}])]
        [:div.kanban-card-footer-attribute
         (when footer-2
           [attribute-view
            {:entity          entity
             :show-labels?    show-labels?
             :show-full-text? show-full-text?
             :attribute       footer-2
             :max-length      max-length}])]
        [:div.show-owner
         (when-not hide-owner?
           [owner-circle (fu/get-owner entity) owner-tooltip])]])]))

(defn row-renderer
  [props js-args]
  (let [{:keys [cache list-state list-id setup-args card-actions card-classes]} props
        {:keys [key parent style index]}
        (js->clj js-args :keywordize-keys true)
        tenant-schema @session/tenant-schema
        entities (list-entities setup-args list-id)
        entity (when (< index (count entities))
                 (nth entities index))
        classes (when (fn? card-classes)
                  (card-classes entity))
        kanban-args (:kanban-args setup-args)
        {:keys [kanban-state card-settings-state default-attributes grouped-by-state
                default-grouped-by expanded? toggle-expand!]}
        kanban-args
        attributes (-> @card-settings-state
                       :attributes
                       (or default-attributes))
        grouped-by (or @grouped-by-state
                       default-grouped-by)
        group-by-datatype (->> grouped-by
                               (registry/get-field-schema tenant-schema)
                               :field/data-type)
        write-access?
        (and (not (session/is-read-only-user?))
             (not-any? #{group-by-datatype}
                       [:data.type/keyword-multi :data.type/ref-multi :data.type/email-multi])
             (f-permissions/field-write-access? grouped-by
                                                (:entity/type entity)
                                                (fu/pipeline-id entity)))
        entity-id (:db/id entity)
        toggle-expand! (partial toggle-expand! entity-id)
        selected-id (:selected-id @kanban-state)]
    (r/as-element
     [:> js/ReactVirtualized.CellMeasurer
      {:cache        cache
       :column-index 0
       :key          key
       :style        style
       :parent       parent
       :row-index    index}
      [:div
       {:class-name      (fu/cs ""
                                (when (= entity-id selected-id)
                                  "selected"))
        :style           style
        :on-click        (fn [_e]
                           (swap! kanban-state assoc :selected-id entity-id)
                           (toggle-expand!)
                           ;;(.recomputeRowHeights list-component index)
                           )
        :on-double-click (fn [_e]
                           ;; TODO: this should be passed from view
                           (when-not (some #{(:entity/type entity)}
                                           [:entity.type/calculated-indirect-holding])
                             (routing/open-entity entity-id)))
        :draggable       write-access?
        :on-drag-start   (fn [e]
                           (if-not write-access?
                             (do (dg/prevent-and-stop e)
                                 false)
                             (do
                               (aset e "dataTransfer" "effectAllowed" (first ALLOWED-DROP-EFFECTS))
                               (oops/ocall (oops/oget e "dataTransfer") "setData" "text" (str index))
                               (swap! kanban-state assoc
                                      :drag-id entity-id
                                      :drag-entity entity
                                      :drag-index index
                                      :drag-source-list list-id))))
        :on-drag-end     (fn [_e])}
       (when entity
         [kanban-card
          {:entity         entity
           :classes        classes
           :long-card?     (expanded? entity-id)
           :list-id        list-id
           :list-state     list-state
           :setup-args     setup-args
           :card-actions   card-actions
           :attributes     attributes
           :toggle-expand! toggle-expand!}])]])))

(defn list-element
  [{:keys [list-id setup-args card-actions card-classes]}]
  (let [cache (js/ReactVirtualized.CellMeasurerCache.
               #js
                {:defaultHeight 150
                        ;;:min-height 100
                 :fixed-width   true
                 :fixedWidth    true})
        lists-state (->> setup-args
                         :kanban-args
                         :lists-state)
        sorted-kanban-entities-state (->> setup-args
                                          :kanban-args
                                          :sorted-kanban-entities-state)
        _ (swap! lists-state assoc-in [list-id :cache] cache)]
    (r/create-class
     {:component-will-unmount
      (fn []
        ;;at least (swap! lists-state dissoc list-id)
        )
      :reagent-render
      (fn [{:keys [list-id setup-args]}]
        ;; used to force re-render(?) => ignore linting warning:
        #_{:clj-kondo/ignore [:unused-value]}
        (count @sorted-kanban-entities-state)
        [:> js/ReactVirtualized.AutoSizer
         (fn [m]
           (let [autosizer-height (aget m "height")
                 c (count (list-entities setup-args list-id))]
             (r/as-element
              (into
               [:> js/ReactVirtualized.List
                (merge
                 {:class-name                 "pipeline-list"
                  :row-count                  c
                  :width                      300
                  :onScrollbarPresenceChange  (fn [js-args]
                                                (let [{:keys [vertical]} (js->clj js-args
                                                                                  :keywordize-keys
                                                                                  true)]
                                                  (swap! lists-state assoc-in
                                                         [list-id :vertical-scrollbar?]
                                                         vertical)))
                  :height                     autosizer-height
                  :deferred-measurement-cache cache
                  :row-height                 (oops/oget cache "rowHeight")
                  :row-width                  300
                  :row-renderer               (partial row-renderer
                                                       {:cache        cache
                                                        :list-state   (r/cursor lists-state [list-id])
                                                        :setup-args   setup-args
                                                        :card-actions card-actions
                                                        :card-classes card-classes
                                                        :list-id      list-id})
                  :ref                        (fn [component]
                                                ;;(when (and component
                                                ;;           (not (= component (get-in @db/lists [list-id :list-component]))))
                                                (swap! lists-state assoc-in
                                                       [list-id :list-component]
                                                       component))})]))))])})))

(defn get-grouped-by
  [{:keys [grouped-by-state default-grouped-by]}]
  (or (some-> grouped-by-state deref)
      default-grouped-by))

(def stage-attribute->entity-type
  (set/map-invert pipeline/entity->stage-attribute))

(def stage-attribute->access-fn
  {:fundraising-opportunity/opportunity-stage session/froppo-creation-flow?
   :sales-opportunity/stage session/sales-oppo-creation-flow?
   ;; update when creation flow implemented for oppo and exit-oppo:
   :oppo/stage session/deal-creation-flow?
   :exit-opportunity/stage session/exit-oppo-creation-flow?})

;; new opportuntity creation flow:
(defn create-opportunity-button
  [{:keys [stage-attribute stage-id]}]
  (when-let [access-fn (get stage-attribute->access-fn stage-attribute)]
    (when (and (not (session/is-read-only-user?))
               (fn? access-fn)
               (access-fn))
      (let [opportunity-type (get stage-attribute->entity-type stage-attribute)
            opportunity-type-label (-> opportunity-type
                                       loc/tr
                                       str
                                       string/lower-case)
            stage-pipeline-attribute (pipeline/pipeline-stage-key opportunity-type)
            pipeline (->> stage-id
                          session/get-entity
                          stage-pipeline-attribute
                          session/get-entity)
            creator-config (:pipeline/creator-config pipeline)
            stages (->> (session/pipeline-stages pipeline)
                        (sort-by :common/ord)
                        doall)
            stage-create-config (ecu/resolve-prevent-stage-config
                                 {:config creator-config
                                  :stages stages})
            prevent-create-setting (get-in stage-create-config
                                           [:prevent-create? :stages stage-id :setting])
            disabled? (= prevent-create-setting :prevent)
            tooltip-text (if disabled?
                           (loc/tr :creator/create-prevented-for-stage)
                           (loc/tr :creator/create-opportunity-for-stage
                                   [opportunity-type-label]))]
        [button/helper-button
         {:tooltip tooltip-text
          :disabled? disabled?
          :on-click (fn [_]
                      (f-entity/open-creator!
                       opportunity-type
                       {:skip-success-msg? true
                        :pipeline pipeline
                        :stage (session/get-entity stage-id)}))}
         [:i.fal.fa-plus]]))))

(defn list-container
  [{:keys [setup-args card-actions card-classes list-entities list-id list-label handle-drop!] :as _args}]
  (let [kanban-state (-> setup-args
                         :kanban-args
                         :kanban-state)
        fundraising? (->> list-entities
                          (first)
                          :entity/type
                          (= :entity.type/fundraising-opportunity))
        grouped-by @(r/track get-grouped-by (:kanban-args setup-args))
        sales? (->> list-entities
                    (first)
                    :entity/type
                    (= :entity.type/sales-opportunity))]
    [:div
     {:class-name    (fu/cs "dropzone"
                            "kanban-list-container"
                            "h-full w-[300px] "
                            (when (and (some? (:drop-list-id @kanban-state))
                                       (= (:drop-list-id @kanban-state) list-id))
                              "dropping"))
      :on-drag-over  (fn [e]
                       (dg/prevent-default e)
                       (swap! kanban-state assoc :drop-list-id list-id))
      :on-drag-leave (fn [_e]
                       (when (= (:drop-list-id @kanban-state) list-id)
                         (swap! kanban-state dissoc :drop-list-id)))
      :on-drop       (fn [e]
                       (dg/prevent-default e)
                       ;; DRAG-DROP-CONTENT-TYPE makes sure only cards are allowed to be dropped
                       (let [{:keys [drag-id drag-entity drag-index drag-source-list drop-list-id]} @kanban-state
                             drop-effect (-> e
                                             (oops/oget "dataTransfer")
                                             (oops/oget "effectAllowed"))]
                         (when (and drag-id
                                    (or (some #{drop-effect} ALLOWED-DROP-EFFECTS)
                                        (= drop-effect "none")))
                           (when drop-list-id
                             (handle-drop! {:setup-args     setup-args
                                            :source-list-id drag-source-list
                                            :dest-list-id   drop-list-id
                                            :drag-id        drag-id
                                            :drag-entity    drag-entity
                                            :drag-index     drag-index})
                             (swap! kanban-state dissoc :drop-list-id)))))}
     [:div
      {:class "py-2 flex justify-between items-center gap-2"}
      [:div
       {:class "text-base-light-text-paler truncate uppercase text-xs font-bold"}
       list-label]
      [:div
       {:class "flex"}
       (cond
         fundraising?
         [:span
          {:class "px-2 py-0.5 inline-flex gap-2 text-xs font-semibold bg-complement-light rounded"}
          (->> list-entities
               (keep :fundraising-opportunity/cur-min-commitment-amount)
               (map fu/bigdec->float)
               (apply +)
               (cu/flip fu/format-number-with-mult "M"))
          [:i.fal.fa-exchange-alt]
          (->> list-entities
               (keep :fundraising-opportunity/cur-max-commitment-amount)
               (map fu/bigdec->float)
               (apply +)
               (cu/flip fu/format-number-with-mult "M"))]
         sales?
         [:span
          {:class "px-2 py-0.5 text-xs font-semibold bg-complement-light rounded"}
          (->> list-entities
               (keep :sales-opportunity/cur-estimated-ARR)
               (map fu/bigdec->float)
               (apply +)
               (cu/flip fu/format-number-with-mult "k"))]
         :else
         [:span
          {:class "px-2 py-0.5 text-xs font-semibold bg-complement-light rounded"}
          (count list-entities)])
       [create-opportunity-button
        {:stage-attribute grouped-by
         :stage-id list-id}]]]
     [:div
      {:class "h-full pb-10
               border border-solid border-base-light-border-paler
               bg-complement-light rounded"}
      [list-element
       {:list-id      list-id
        :card-actions card-actions
        :card-classes card-classes
        :setup-args   setup-args}]]]))

(defn handle-drop!
  [{:keys [setup-args source-list-id dest-list-id drag-entity]}]
  (let [kanban-args (:kanban-args setup-args)
        {:keys [sorted-kanban-entities-state grouped-by-state default-grouped-by]} kanban-args
        attribute (or @grouped-by-state                                                             ;;grouping by this attribute
                      default-grouped-by)
        grid-entities @sorted-kanban-entities-state
        grid-index (->> grid-entities
                        (map-indexed (fn [idx entity]
                                       (when (= (:db/id entity) (:db/id drag-entity))
                                         idx)))
                        (remove nil?)
                        (first))
        apply-optimistic! (fn []
                            (swap! sorted-kanban-entities-state assoc-in
                                   [grid-index attribute] dest-list-id)
                            (js/setTimeout (partial recompute-all! {:setup-args setup-args}) 50))
        retract! (fn [e]
                   (let [updated-entity (assoc drag-entity attribute dest-list-id)
                         updated-entities @sorted-kanban-entities-state
                         c (count updated-entities)
                         entity-index (.indexOf updated-entities updated-entity)]
                     (logging/error (str attribute " update failed!") e)
                     (when (< -1 entity-index c)
                       (swap! sorted-kanban-entities-state assoc-in
                              [grid-index attribute]
                              source-list-id))))]
    (cond
      (neg? grid-index)
      (logging/error (str "Negative grid index : " drag-entity " >> " attribute "->> " source-list-id " > " dest-list-id))

      (nil? dest-list-id)
      nil

      ;; Stage column move: route through the entry-criteria flow. Optimistic
      ;; update fires only when the save actually fires (via :before-save!),
      ;; so a criteria-modal cancel leaves the card in its source column.
      (pipeline/stage-attribute? attribute)
      (f-entity/attempt-move-to-stage!
       {:opportunity drag-entity
        :target-stage dest-list-id
        :before-save! apply-optimistic!
        :on-error! retract!})

      :else
      (do
        (apply-optimistic!)
        (entity-actions/*->field-update! (:db/id drag-entity) attribute dest-list-id
                                         (fn [_]) retract!)))))

(defn resolve-kanban-grouping
  [setup-args]
  (let [{:keys [kanban-args containing-entity]} setup-args
        {:keys [grouped-by-state default-grouped-by]} kanban-args
        attribute (or (and grouped-by-state @grouped-by-state)
                      default-grouped-by)
        tenant-schema @session/tenant-schema
        fs (registry/get-field-schema tenant-schema attribute)
        grouping-options (cond
                           (some (set (:field/entity-types fs)) [:entity.type/named-value])
                           (fu/named-value-options fs (pipeline/entity-pipeline containing-entity))
                           (= (:value-type fs) :entity)
                           (fu/option-entities containing-entity fs)
                           (= (:value-type fs) :keyword)
                           (:options fs)
                           :else
                           [])
        not-set (list-entities setup-args nil)
        all-options (if (seq not-set)
                      (concat grouping-options [nil])
                      grouping-options)
        grouped-options (->> all-options
                             (map (fn [option]
                                    (let [list-id (or (:db/id option)
                                                      (:keyword option)
                                                      (:key option)
                                                      nil)
                                          list-entities (list-entities setup-args list-id)]
                                      [list-id list-entities]))))]
    [attribute grouped-options all-options]))

(defn has-empty-groups?
  [setup-args]
  (->> (resolve-kanban-grouping setup-args)
       (second)
       (some (fn [[_list-id list-entities]]
               (empty? list-entities)))))

(defn view
  [{:keys [setup-args card-actions card-classes]}]
  (let [{:keys [kanban-args]} setup-args
        {:keys [kanban-state sorted-kanban-entities-state]} kanban-args]
    (add-watch sorted-kanban-entities-state
               :kanban-entities
               (fn [_k _a old new]
                 (when (not= old new)
                   (recompute-all! {:setup-args setup-args}))))
    (add-watch kanban-state
               :card-mode
               (fn [_k _a old new]
                 (let [old-mode (:card-mode old)
                       new-mode (:card-mode new)
                       old-individual (:expanded old)
                       new-individual (:expanded new)]
                   (when (or (not= old-mode new-mode)
                             (not= old-individual new-individual))
                     (recompute-all! {:setup-args setup-args})))))
    (r/create-class
     {:component-will-unmount (fn []
                                ;;cleanup
                                (remove-watch sorted-kanban-entities-state :kanban-entities)
                                (remove-watch kanban-state :card-mode))
      :display-name           "Kanban view"
      :reagent-render         (fn [{:keys [setup-args]}]
                                (let [[group-by-attribute all-grouped-entities all-options]
                                      (resolve-kanban-grouping setup-args)
                                      non-empty-grouped-entities (->> all-grouped-entities
                                                                      (remove (fn [[_list-id list-entities]]
                                                                                (empty? list-entities))))
                                      grouped-entities (if (:hide-all-empty-groups? @kanban-state)
                                                         non-empty-grouped-entities
                                                         all-grouped-entities)
                                      show-all? (or (:show-all-groups? @kanban-state)
                                                    (<= (count grouped-entities) 20))
                                      shown-grouped-entities (if show-all?
                                                               grouped-entities
                                                               (take 20 grouped-entities))]
                                  [:div
                                   {:class "h-full flex flex-auto"}
                                   [:div
                                    {:class "flex-auto overflow-hidden"}
                                    (when-not show-all?
                                      [:div
                                       {:class "flex flex-row gap-2 bg-warning-regular text-warning-regular-text
                                       max-w-[100vw]"}
                                       [:div
                                        {:class "flex flex-row gap-2 p-2"}
                                        [button/secondary-solid
                                         {:on-click (fn [_e]
                                                      (swap! kanban-state assoc :show-all-groups? true)
                                                      (deref kanban-state))}
                                         (loc/tr :show-all-kanban-groups-btn)]
                                        [:span.warning-banner__text
                                         (loc/tr :show-all-kanban-groups [(count shown-grouped-entities)])]]])
                                    [:div {:class "flex gap-3 h-full"}
                                     (->>
                                      shown-grouped-entities
                                      (map (fn [[list-id list-entities]]
                                             (let [list-label (or (->> list-id
                                                                       (get @session/entities)
                                                                       entity/display-string)
                                                                  (->> list-id
                                                                       (fu/find-option-label all-options))
                                                                  (loc/tr :not-set))]
                                               ^{:key (str "list-" group-by-attribute "-" list-id)}
                                               [list-container
                                                {:list-id       list-id
                                                 :list-entities list-entities
                                                 :list-label    list-label
                                                 :card-actions  card-actions
                                                 :card-classes  card-classes
                                                 :setup-args    setup-args
                                                 :handle-drop!  handle-drop!}])))
                                      (doall))]]]))})))

(defn preview-value
  [fs]
  (let [data-type (:field/data-type fs)]
    (cond (some #{data-type} numerics)
          "xxxxxx"
          :else
          "xxxxxxxxx xxxxxxxxxx xxxxxxxxx")))

(defn attribute-preview
  [attribute show-labels? show-full-text? max-length]
  (let [;;raw-value (get entity attribute)
        tenant-schema @session/tenant-schema
        fs (registry/get-field-schema tenant-schema attribute)
        value (preview-value fs)
        str-value (str value)
        attribute-tooltip (attribute/attribute-label tenant-schema attribute)
        value (if (and (not show-full-text?)
                       (> (count str-value) max-length))
                (str (subs str-value 0 max-length) "...")
                str-value)
        label (or (-> fs
                      :field/translations
                      :en)
                  (:field/name fs)
                  (loc/tr attribute))
        secondary-attribute (:field/secondary-attribute fs)
        secondary-fs (when secondary-attribute
                       (registry/get-field-schema tenant-schema secondary-attribute))
        secondary-value (when secondary-attribute
                          (when (= (:field/data-type secondary-fs) :data.type/currency-symbol)
                            (let [;;TODO randomize value?:
                                  currency-kw :eur
                                  tenant-currency-kw (:tenant/default-currency @session/tenant)]
                              ;;show currency only if differs from tenant-currency:
                              (when (and currency-kw
                                         (not= currency-kw tenant-currency-kw))
                                (currencies/get-label currency-kw)))))]
    [:div
     {:class "pb-3 flex flex-col justify-between text-xs"}
     (when show-labels?
       [:div
        {:class "block py-1 text-base-light-text-paler"}
        label])
     (fu/overlay-if (not show-labels?)
                    [rbs/OverlayTrigger
                     {:placement     "top"
                      :popper-config {:modifiers [{:name    "preventOverflow"
                                                   :options {:main-axis false}}
                                                  {:name "hide"}]}
                      :overlay       (r/as-element [rbs/Tooltip
                                                    {:class "in"
                                                     :id    (str "attribute-tooltip-" attribute)}
                                                    attribute-tooltip])}]
                    [:div
                     value
                     [:span
                      (when secondary-value
                        (str " " secondary-value))]])]))

(defn card-preview
  [{:keys [_kanban-args _entity-type]}]
  (fn [{:keys [kanban-args entity-type]}]
    (let [example-title (loc/tr :name)
          tenant-schema @session/tenant-schema
          preview-entity {:db/id       "preview-entity"
                          :entity/type entity-type}
          preview-owner {:account/first-name "Owner"
                         :account/last-name  "Preview"
                         :db/id              "preview-owner"
                         :entity/type        :entity.type/account}
          owner-name (entity/display-string preview-owner)
          owner-text (loc/tr :entity-type-owner [(loc/tr (:entity/type preview-entity))])
          owner-tooltip (str owner-text ": " owner-name)
          {:keys [card-settings-state default-attributes]} kanban-args
          card-settings @card-settings-state
          {:keys [attributes hide-owner? footer-1 footer-2 show-labels? show-full-text?
                  max-length]}
          card-settings
          show-labels? (cu/default-boolean true show-labels?)
          max-length (or max-length 500)
          attributes (or attributes default-attributes)
          visible-attributes (->> attributes
                                  ;; do not show secondary attributes standalone
                                  (remove (fn [a]
                                            (->> (registry/get-field-schema tenant-schema a)
                                                 :field/secondary-field?))))]
      [:div
       [:div
        {:class "p-3
                   border border-solid border-base-light-border-paler
                   bg-base-regular rounded"}
        [:div
         {:class "flex justify-between"}
         [:div
          {:class "w-full truncate font-semibold"}
          example-title]
         [button/square-secondary-open
          {:class "invisible"}
            ;;TODO [:i.fal.fa-ellipsis-v] if more than 1 action:
          [:i.fal.fa-folder-open]]]
        [:div
         {:class "pt-3 min-h-8"}
         (for [attribute visible-attributes]
           ^{:key (str "long-card-" attribute)}
           [attribute-preview attribute show-labels? show-full-text? max-length])]
        [:div
         {:class "mt-4 w-full flex justify-between gap-3"}
         ;;two numeric views
         [:div
          (when footer-1
            [attribute-preview footer-1 show-labels? show-full-text? max-length])]
         [:div
          (when footer-2
            [attribute-preview footer-2 show-labels? show-full-text? max-length])]
         [:div
          (when-not hide-owner?
            [owner-circle preview-owner owner-tooltip])]]]])))

(defn attribute-setup
  [{:keys [_attributes _attribute _label _update-card-settings!
           _drag-state _on-drag-start! _on-drag-end! _on-drag-leave _on-drag-enter]}]
  (let [local-atom (r/atom {:drop-count   0
                            :drag-counted #{}})]
    (fn [{:keys [attributes attribute label update-card-settings!
                 drag-state on-drag-start! on-drag-end! on-drag-leave on-drag-enter]}]
      (let [drop-count (:drop-count @local-atom)
            id attribute
            {:keys [drag-id drag-over drop]} @drag-state]
        [:div
         {:class-name    (fu/cs "pl-6 w-full flex relative group"
                                ""
                                (when (= drag-id id)
                                  "bg-base-regular")
                                (when (and (> drop-count 0)
                                           (some? drag-id)
                                           (not= drag-id id))
                                  (str "dropzone"
                                       (if (= drop :above)
                                         " above"
                                         " below"))))
          :draggable     true
          :on-drag-start (fn [e]
                           (aset e "dataTransfer" "effectAllowed" "move")
                           ;;drag data must be set for dragend to fire in firefox:
                           (oops/ocall (oops/oget e "dataTransfer") "setData" "text" "zapflow")

                           (on-drag-start! id)
                           (dg/stop-propagation e))
          :on-drag-end   (fn [e]
                           (dg/prevent-and-stop e)
                           (swap! local-atom assoc :drop-count 0)
                           (on-drag-end! nil))
          :on-drop       (fn [e]
                           (dg/prevent-and-stop e)
                           (on-drag-end! drag-over))
          :on-drag-leave (fn [oe]
                           (let [target (->> oe
                                             .-target)
                                 already-counted (some #{(str target)} (:drag-counted @local-atom))
                                 new-count (dec (:drop-count @local-atom))]
                             (swap! local-atom assoc
                                    :drag-counted
                                    (disj (:drag-counted @local-atom) (str target)))
                             (when already-counted
                               (swap! local-atom assoc :drop-count new-count))

                             (when (= new-count 0)
                               (on-drag-leave id))
                             (dg/prevent-and-stop oe)))
          :on-drag-over  dg/prevent-default
          :on-drag-enter (fn [ee]
                           (let [target (->> ee
                                             .-target)
                                 already-counted (some #{(str target)} (:drag-counted @local-atom))
                                 new-count (inc (:drop-count @local-atom))]
                             (swap! local-atom assoc
                                    :drag-counted
                                    (conj (:drag-counted @local-atom) (str target)))
                             (when-not already-counted
                               (swap! local-atom assoc :drop-count new-count))
                             (when (= new-count 1)
                               (on-drag-enter id))
                             (dg/prevent-and-stop ee)))}
         [:div
          {:class-name (fu/cs "flex justify-center items-center w-6 absolute left-0 top-1/2 -translate-y-1/2 invisible group-hover:visible" ;;"card__handle"
                              "cursor-pointer")}
          [:i.fal.fa-grip-vertical]]
         [:div
          {:class "w-full p-3 mb-0 flex items-center justify-between
                   text-sm
                   border border-solid border-base-light-border-paler rounded"}
          [:span
           {:class "font-semibold"}
           label]
          [:i.fal.fa-times
           {:class    "cursor-pointer"
            :on-click (fn [_]
                        (let [new-attributes (->> attributes
                                                  (remove #{attribute})
                                                  vec)]
                          (update-card-settings! :attributes new-attributes)))}]]]))))

(defn open-select-modal
  [on-select! available-attributes]
  (let [tenant-schema @session/tenant-schema
        options (->> available-attributes
                     (map (fn [k]
                            {:value k
                             :label (attribute/attribute-label tenant-schema k)}))
                     (sort-by :label))]
    (modals/open-modal!
     dynamic/modal-key
     {:title (loc/tr :select-attribute-to-show)
      :render-fn
      (fn [modal-state close-modal!]
        (let [value (:value @modal-state)
              button-disabled? (nil? value)]
          [:div
           [select/view
            {:options     options
             :value       (->> options
                               (cu/filter-by :value value)
                               (first))
             :placeholder (loc/tr :select)
             :on-change   (fn [v _opt]
                            (swap! modal-state assoc :value v))}]
           [:div.flex.justify-center.gap-3.mt-4
            [button/secondary-hollow
             {:on-click (fn [_]
                          (close-modal!))}
             (loc/tr :cancel)]
            [button/primary-solid
             {:disabled? button-disabled?
              :on-click  (fn [_]
                           (when-not button-disabled?
                             (on-select! value)
                             (close-modal!)))}
             (loc/tr :add)]]]))})))

;;only date, currency, numeric, rating
(defn footer-attribute?
  [tenant-schema attribute]
  (let [fs (registry/get-field-schema tenant-schema attribute)
        data-type (:field/data-type fs)]
    (or (some #{data-type} numerics)
        (some #{attribute} [:oppo/ratings]))))

(defn card-setup-optional
  [{:keys [_kanban-args _ls-path]}]
  (fn [{:keys [kanban-args ls-path]}]
    (let [{:keys [card-settings-state]} kanban-args
          card-settings @card-settings-state
          {:keys [max-length show-labels? show-full-text?]} card-settings
          show-labels? (cu/default-boolean true show-labels?)
          max-length (or max-length 500)
          update-card-settings!
          (fn [attribute value]
            (if (nil? value)
              (swap! card-settings-state dissoc attribute)
              (swap! card-settings-state assoc attribute value))
            @card-settings-state
            (if (nil? value)
              (ls/update-in (ls-path) potpuri/dissoc-in [:card-settings attribute])
              (ls/update-in (ls-path) assoc-in [:card-settings attribute] value)))]
      [:div.card-setup
       [:div.kanban-card-setup.pt-4.pl-4
        [:h2.mb-3.text-bold (loc/tr :kanban-card-config-styling-heading)]
        [:div.mb-2.d-flex.align-items-center
         [:div
          {:class "flex gap-3 items-center"}
          [toggle/toggle
           {:checked?  show-labels?
            :on-change (fn [toggled?]
                         (update-card-settings! :show-labels? toggled?))}]
          [:span (loc/tr :kanban-card-show-labels)]]]
        [:div
         {:class "flex gap-3 items-center"}
         [toggle/toggle
          {:checked?  (not show-full-text?)
           :on-change (fn [_]
                        (update-card-settings! :show-full-text? (not show-full-text?)))}]
         [:span
          (loc/tr :kanban-card-show-limit-the-amount)]]
        (when-not show-full-text?
          [:div
           [:div
            {:class "flex items-center gap-2"}
            [:span
             (loc/tr :kanban-card-show-max)]
            [data-input/view
             {:data-type :data.type/integer
              :class     "w-16"
              :value     max-length
              :on-blur   (fn [val]
                           (update-card-settings! :max-length val))}]
            [:span
             (loc/tr :kanban-card-show-characters)]]])]])))

(defn card-setup
  [{:keys [_kanban-args _ls-path]}]
  (let [drag-state (r/atom {})]
    (fn [{:keys [kanban-args ls-path] :as setup-args}]
      (let [{:keys [card-settings-state default-attributes]} kanban-args
            card-settings @card-settings-state
            {:keys [attributes hide-owner? footer-1 footer-2]} card-settings
            attributes (or attributes default-attributes)
            tenant-schema @session/tenant-schema
            visible-attributes (->> attributes
                                    ;; do not show secondary attributes standalone
                                    (remove (fn [a]
                                              (->> a
                                                   (registry/get-field-schema tenant-schema)
                                                   :field/secondary-field?))))
            update-card-settings!
            (fn [attribute value]
              (if (nil? value)
                (swap! card-settings-state dissoc attribute)
                (swap! card-settings-state assoc attribute value))
              (if (nil? value)
                (ls/update-in (ls-path) potpuri/dissoc-in [:card-settings attribute])
                (ls/update-in (ls-path) assoc-in [:card-settings attribute] value)))]
        [:div
         [:div
          [:div
           {:class "flex flex-col gap-4"}
           [:h4
            {:class "pl-6 font-bold"}
            [:strong (loc/tr :card-setup-attributes-info)]]
           [:div
            {:class "flex flex-col gap-1"}
            (for [attribute visible-attributes
                  :let [label (attribute/attribute-label tenant-schema attribute)]]
              ^{:key (str "attribute-setup-" attribute)}
              [attribute-setup
               {:attributes            visible-attributes
                :card-settings-state   card-settings-state
                :attribute             attribute
                :update-card-settings! update-card-settings!
                :label                 label
                :drag-state            drag-state
                :on-drag-start!        (fn [_arg]
                                         (swap! drag-state assoc :drag-id attribute))
                :on-drag-end!          (fn [drop-target]
                                         (let [drag-id (:drag-id @drag-state)
                                               drop (:drop @drag-state)]
                                           (when (and drag-id
                                                      drop-target
                                                      (not= drag-id drop-target))
                                             (let [ordered attributes
                                                   items (remove #{drag-id} ordered)
                                                   head (take-while #(not= % drop-target) items)
                                                   tail (rest (drop-while #(not= % drop-target)
                                                                          items))
                                                   middle (if (= drop :below)
                                                            [drop-target drag-id]
                                                            [drag-id drop-target])
                                                   new-order (vec (concat head middle tail))]
                                               (update-card-settings! :attributes new-order)))
                                           (swap! drag-state dissoc :drag-id :drag-over)))
                :on-drag-leave         (fn [arg]
                                         (when (and (not= arg (:drag-id @drag-state))
                                                    (= arg (:drag-over @drag-state)))
                                           (swap! drag-state dissoc :drag-over)))
                :on-drag-enter         (fn [arg]
                                         (let [drag-id (:drag-id @drag-state)
                                               dragging-order (.indexOf attributes drag-id)
                                               drag-enter-order (.indexOf attributes arg)]
                                           (when (not= arg drag-id)
                                             (swap! drag-state assoc :drag-over arg)
                                             (if (> dragging-order drag-enter-order)
                                               (swap! drag-state assoc :drop :above)
                                               (swap! drag-state assoc :drop :below)))))}])]
           [:div
            {:class "pl-6"}
            [button/secondary-hollow
             {:on-click (fn [_]
                          (open-select-modal
                           (fn [value]
                             (when (and (some? value)
                                        (not (contains? (set attributes) value)))
                               (update-card-settings! :attributes
                                                      (vec (concat attributes [value])))))
                           (->> (grid/resolve-column-key-universe setup-args)
                                (remove (set attributes))
                                (remove (fn [a]
                                          (->> a
                                               (registry/get-field-schema tenant-schema)
                                               :field/secondary-field?))))))}
             [:i.fal.fa-plus] [:span (loc/tr :select-attribute-to-add)]]]]
          [:div
           {:class "pl-6 w-full mt-6 grid grid-cols-3"}
           [:div
            {:class "flex justify-center items-center
                     border border-dashed border-base-light-border-paler h-12"}
            (if footer-1
              [:div
               {:class "p-2 w-full flex justify-between items-center"}
               (attribute/attribute-label tenant-schema footer-1)
               [:i.fal.fa-times.clickable
                {:class    "cursor-pointer"
                 :on-click (fn [_]
                             (update-card-settings! :footer-1 nil))}]]
              [button/square-secondary-open
               {:tooltip  (loc/tr :select-attribute-to-show)
                :class    "w-full h-full"
                :on-click (fn [_]
                            (open-select-modal
                             (fn [value]
                               (update-card-settings! :footer-1 value))
                             (->> (grid/resolve-column-key-universe setup-args)
                                  (filter (fn [a]
                                            (footer-attribute? tenant-schema a))))))}
               [:i.fal.fa-plus]])]
           [:div
            {:class "flex justify-center items-center
                     border border-dashed border-base-light-border-paler h-12"}
            (if footer-2
              [:div
               {:class "p-2 w-full flex justify-between items-center"}
               (attribute/attribute-label tenant-schema footer-2)
               [:i.fal.fa-times
                {:class    "cursor-pointer"
                 :on-click (fn [_]
                             (update-card-settings! :footer-2 nil))}]]
              [button/square-secondary-open
               {:class    "w-full h-full"
                :tooltip  (loc/tr :select-attribute-to-show)
                :on-click (fn [_]
                            (open-select-modal
                             (fn [value]
                               (update-card-settings! :footer-2 value))
                             (->> (grid/resolve-column-key-universe setup-args)
                                  (filter (fn [a]
                                            (footer-attribute? tenant-schema a))))))}
               [:i.fal.fa-plus]])]
           [:div
            {:class "flex justify-center items-center
                     border border-dashed border-base-light-border-paler h-12"}
            [:div
             {:class "p-2 w-full flex justify-center items-center"}
             [switch/view
              {:class-name (fu/cs "show-owner-switch"
                                  (if hide-owner?
                                    "show-owner-switch-hide"
                                    "show-owner-switch-show"))
               :options    [{:key        "on"
                             :label      (if-not hide-owner?
                                           (loc/tr :owner-shown)
                                           "")
                             :selected?  (not (boolean hide-owner?))
                             :on-select! (fn [_]
                                           (update-card-settings! :hide-owner? true))}
                            {:key        "off"
                             :label      (if hide-owner?
                                           (loc/tr :owner-hidden)
                                           "")
                             :selected?  (boolean hide-owner?)
                             :on-select! (fn [_]
                                           (update-card-settings! :hide-owner? nil))}]}]]]]]]))))

(defn card-settings-view
  [setup-args]
  [:div
   {:class "auto flex-auto"}
   [:div
    {:class-name "container"}
    [:div
     {:class-name "grid grid-cols-10 gap-8 py-4 px-4"}
     [:div
      {:class-name "col-span-10 md:col-span-5 lg:col-span-6"}
      [:div.card-settings
       [card-setup setup-args]]]
     [:div
      {:class-name "col-span-10 md:col-span-5 lg:col-span-4"}
      [card-preview setup-args]
      [card-setup-optional setup-args]]]]])

(defn open-entity-action
  [{:keys [entity-type]}]
  (let [action-id (str "open-" (name entity-type))]
    {:id         action-id
     :label      (loc/tr :open)
     :icon       "fal fa-folder-open"
     :href       (fn [{:keys [entity]}]
                   (if-let [entity-id (:db/id entity)]
                     (routing/path-for entity-type :id entity-id)
                     (logging/warn "Kanban open action -> entity id missing")))
     :on-select! (fn [{:keys [entity]}]
                   (routing/open-entity (:db/id entity)))}))
